/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { UeElement } from '@oneaudi/falcon-tools';
import React, { useContext, useState } from 'react';
import { ContextServiceTracking } from '../FeatureApp';
import { Picture } from './Picture';
import { MaybeLink } from './MaybeLink';
import {
  CustomTextLink,
  CustomText,
  StyledBanner,
  TextLayout,
  TextWrapper,
  CtaWrapper,
} from './style';
import { hasAnyImage, onClick, trackEvent, setCopyOrder } from '../utils';
import { Headline } from '@oneaudi/unified-web-components';
import { AudiIcon } from './AudiIcon';
import type { TrackingServiceV2 } from '../feature-service-types';
import type { BannerLayout, Theme } from '../../@types/Content';

type BannerProps = {
  alignment: string;
  ctaUrl?: string;
  imageAlt: string;
  imageSrcLarge: string;
  imageSrcMedium?: string;
  imageSrcSmall?: string;
  fullWidth: boolean;
  copy: string;
  ctaLabel: string;
  headline: string;
  headlineHierarchy: 'h1' | 'h2' | 'h3' | 'h4';
  icon: boolean;
  imgAlignment: string;
  ctaAlt: string;
  textAlignment: 'left' | 'center';
  bannerLayout: BannerLayout;
  theme: Theme;
};

export const Banner = ({
  alignment,
  ctaUrl,
  imageAlt,
  imageSrcLarge,
  imageSrcMedium,
  imageSrcSmall,
  fullWidth,
  copy,
  ctaLabel,
  ctaAlt,
  headline,
  headlineHierarchy,
  icon,
  imgAlignment,
  textAlignment,
  bannerLayout,
}: BannerProps) => {
  const [mediaSize, setMediaSize] = useState<'Small' | 'Medium' | 'Large'>('Small');
  const [headlineOrder, setHeadlineOrder] = useState<'3' | '4'>('4');
  const serviceTracking = useContext(ContextServiceTracking);
  const ueElementImageProperty = `assetImage${mediaSize}`;
  const hasText = Boolean(headline?.trim() || copy?.trim());
  const viewport = React.useRef('Small');
  const showIcon = bannerLayout === 'textOnly' ? icon : false;
  const layout = !hasText ? 'imageOnly' : bannerLayout;

  React.useEffect(() => {
    const updateSize = () => {
      const width = window.innerWidth;

      let size: 'Small' | 'Medium' | 'Large' = 'Large';
      let order: '3' | '4' = '4';

      if (width <= 767) {
        size = 'Small';
      } else if (width <= 1023) {
        size = 'Medium';
      } else {
        size = 'Large';
      }

      order = width >= 1440 ? '3' : '4';

      setMediaSize(size);
      viewport.current = size;
      setHeadlineOrder(order);
    };

    updateSize();
    window.addEventListener('resize', updateSize);

    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  return (
    <MaybeLink ctaUrl={ctaUrl} fullWidth={fullWidth} ariaLabel={ctaAlt} bannerLayout={layout}>
      <StyledBanner
        alignment={alignment}
        bannerLayout={layout}
        data-testid="Banner"
        fullWidth={fullWidth}
        hasAnyImage={hasAnyImage([imageSrcLarge, imageSrcMedium!, imageSrcSmall!])}
        imgAlignment={imgAlignment}
        onClick={() => {
          onClick(serviceTracking as TrackingServiceV2, ctaUrl as string, imageAlt);
          if (ctaUrl !== undefined) {
            trackEvent(ctaUrl, imageAlt);
          }
        }}
      >
        <UeElement type="media" property={ueElementImageProperty} label={`Image ${mediaSize}`}>
          {(ueImageProps) =>
            hasAnyImage([imageSrcLarge, imageSrcMedium!, imageSrcSmall!]) &&
            layout !== 'textOnly' && (
              <Picture
                imageAlt={imageAlt}
                imageSrcLarge={imageSrcLarge}
                imageSrcMedium={imageSrcMedium}
                imageSrcSmall={imageSrcSmall}
                fullWidth={fullWidth}
                label={`Image ${mediaSize}`}
                bannerLayout={layout}
                {...ueImageProps}
              />
            )
          }
        </UeElement>
        {layout !== 'imageOnly' && (headline || copy) && (
          <TextLayout
            imgAlignment={imgAlignment}
            bannerLayout={layout}
            hasAnyImage={hasAnyImage([imageSrcLarge, imageSrcMedium!, imageSrcSmall!])}
            hasText={hasText}
            data-has-text={hasText ? 'true' : 'false'}
            data-testid="text-layout"
          >
            {showIcon && viewport.current === 'Small' && <AudiIcon />}
            <TextWrapper textAlignment={textAlignment} bannerLayout={layout}>
              {headline && (
                <Headline
                  variant={{
                    order: headlineOrder,
                    type: 'headline',
                    weight: 'normal',
                  }}
                  tag={headlineHierarchy}
                  className="banner-headline"
                >
                  {headline}
                </Headline>
              )}

              {copy && (
                <CustomText
                  variant={{
                    order: setCopyOrder(viewport.current),
                    style: 'normal',
                  }}
                  tag="p"
                >
                  {copy}
                </CustomText>
              )}
            </TextWrapper>
            <CtaWrapper>
              {showIcon && viewport.current !== 'Small' && <AudiIcon />}

              {ctaUrl && ctaLabel && (
                <CustomTextLink
                  href={ctaUrl}
                  size="sm"
                  variant="standalone"
                  aria-label={ctaAlt}
                  target="_self"
                  icon="forward"
                  iconPosition="trailing"
                >
                  {ctaLabel}
                </CustomTextLink>
              )}
            </CtaWrapper>
          </TextLayout>
        )}
      </StyledBanner>
    </MaybeLink>
  );
};
export default Banner;
